/* ==========================================================================
   ZARIF INFINITE SCROLL
   <zarif-infinite-scroll> custom element for Prestige 9.x collection and search pages.
   Markup: snippets/zarif-infinite-scroll.liquid. No dependencies, no Prestige core edits.

   Flow:
   1. Seed a Set of product handles from the first page's <product-card handle="..."> elements.
   2. Fetch the next page with the Section Rendering API (next_url + section_id), parse with DOMParser.
   3. Append only cards whose handle is not already rendered (zero-duplicate guarantee).
   4. Repeat via IntersectionObserver (auto mode) or the Load more button (button mode).
   ========================================================================== */
(() => {
  if (!window.customElements || window.customElements.get('zarif-infinite-scroll')) return;

  const STORAGE_PREFIX = 'zarif-infinite-scroll:';
  const STATE_TTL = 30 * 60 * 1000; // Back button state expires after 30 minutes
  const MAX_EMPTY_HOPS = 3; // Max consecutive pages with 0 new cards before falling back to the button
  const WISHLIST_KEY = 'custom_wishlist'; // Same key as assets/wishlist.js

  const DEBUG = /[?&]zis_debug=1/.test(window.location.search);
  const debug = (...args) => { if (DEBUG) console.log('[zarif-infinite-scroll]', ...args); };

  // --- Helpers --------------------------------------------------------------

  // Absolute URL without section_id or hash, so the same page always maps to the same string
  const normaliseUrl = (href) => {
    if (!href) return null;
    try {
      const url = new URL(href, window.location.origin);
      url.searchParams.delete('section_id');
      url.hash = '';
      return url.toString();
    } catch (error) {
      return null;
    }
  };

  const pageNumberOf = (href) => {
    try {
      return parseInt(new URL(href, window.location.origin).searchParams.get('page'), 10) || 1;
    } catch (error) {
      return 1;
    }
  };

  // Storage key = current collection or search URL plus filters and sort (page param ignored)
  const stateKey = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return STORAGE_PREFIX + url.pathname + url.search;
  };

  const cardKey = (card) => card.getAttribute('handle') || card.getAttribute('data-product-url') || null;

  const prefersMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

  const isBackForward = () => {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      return !!entry && entry.type === 'back_forward';
    } catch (error) {
      return false;
    }
  };

  // --- Custom element -------------------------------------------------------

  class ZarifInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      // Bound handlers so they can be removed in disconnectedCallback
      this.onIntersect = this.onIntersect.bind(this);
      this.onButtonClick = this.onButtonClick.bind(this);
      this.onRetryClick = this.onRetryClick.bind(this);
      this.onFacetUpdate = this.onFacetUpdate.bind(this);
      this.onSectionUnload = this.onSectionUnload.bind(this);
      this.onGridClick = this.onGridClick.bind(this);
      this.saveState = this.saveState.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.getAttribute('section-id');
      this.grid = this.sectionId ? document.getElementById(`product-list-${this.sectionId}`) : null;

      // Guard: exit silently if the expected markup is missing
      if (!this.sectionId || !this.grid) return;

      this.mode = this.getAttribute('mode') === 'button' ? 'button' : 'auto';
      this.offset = Math.min(Math.max(parseInt(this.getAttribute('offset'), 10) || 600, 0), 2000);
      this.totalPages = parseInt(this.getAttribute('pages'), 10) || 1;
      this.startOffset = parseInt(this.getAttribute('start-offset'), 10) || 0;
      this.total = parseInt(this.getAttribute('total'), 10) || 0;
      this.progressTemplate = this.getAttribute('progress-template');
      this.nextUrl = normaliseUrl(this.getAttribute('next-url'));

      this.sentinel = this.querySelector('.zarif-infinite-scroll__sentinel');
      this.loader = this.querySelector('.zarif-infinite-scroll__loader');
      this.button = this.querySelector('.zarif-infinite-scroll__button');
      this.progress = this.querySelector('.zarif-infinite-scroll__progress');
      this.errorBox = this.querySelector('.zarif-infinite-scroll__error');
      this.retryButton = this.querySelector('.zarif-infinite-scroll__retry');
      this.endMessage = this.querySelector('.zarif-infinite-scroll__end');
      this.liveRegion = this.querySelector('.zarif-infinite-scroll__live');

      // Zero-duplicate state
      this.renderedKeys = new Set();
      this.requestedUrls = new Set();
      this.loadedUrls = []; // Pages appended so far, in order (used for back button restore)
      this.isLoading = false;
      this.paused = false; // true after an error or after MAX_EMPTY_HOPS: waits for a user click
      this.emptyHops = 0;
      this.abortController = null;
      this.observer = null;
      this.gridObserver = null;

      this.seed();

      this.button?.addEventListener('click', this.onButtonClick);
      this.retryButton?.addEventListener('click', this.onRetryClick);
      this.grid.addEventListener('click', this.onGridClick, true);
      document.addEventListener('facet:update', this.onFacetUpdate);
      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      window.addEventListener('pagehide', this.saveState);

      this.watchGrid();

      if (!this.nextUrl) {
        this.finish();
        return;
      }

      if (this.hasAttribute('hide-footer')) {
        document.documentElement.classList.add('zarif-is-hide-footer');
      }

      this.updateProgress();

      // Back button: rebuild previously loaded pages first, then start observing
      this.restore().then((restored) => {
        if (!this.isConnected) return;
        debug(restored ? 'restored state' : 'fresh init', this.renderedKeys.size, 'cards');
        this.arm();
      });
    }

    disconnectedCallback() {
      this.teardown();
    }

    // Disconnect observers, abort requests and remove every listener (no memory leaks)
    teardown() {
      this.abortController?.abort();
      this.abortController = null;
      this.observer?.disconnect();
      this.observer = null;
      this.gridObserver?.disconnect();
      this.gridObserver = null;
      this.button?.removeEventListener('click', this.onButtonClick);
      this.retryButton?.removeEventListener('click', this.onRetryClick);
      this.grid?.removeEventListener('click', this.onGridClick, true);
      document.removeEventListener('facet:update', this.onFacetUpdate);
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      window.removeEventListener('pagehide', this.saveState);
      document.documentElement.classList.remove('zarif-is-hide-footer');
      this.isLoading = false;
    }

    // Seed the Set with page 1 cards so they're never appended again
    seed() {
      this.renderedKeys.clear();
      this.grid.querySelectorAll('product-card').forEach((card) => {
        const key = cardKey(card);
        if (key) this.renderedKeys.add(key);
      });
      if (this.nextUrl) this.requestedUrls.add(normaliseUrl(window.location.href)); // Current page counts as requested
    }

    // --- Triggers -----------------------------------------------------------

    arm() {
      if (!this.isConnected) return;
      if (!this.nextUrl) return this.finish();
      if (this.errorBox && !this.errorBox.hidden) return; // Waiting for Retry: don't show a second button

      if (this.mode === 'button' || this.paused) {
        this.showButton(true);
        return;
      }

      this.showButton(false);

      if (!('IntersectionObserver' in window)) {
        this.showButton(true); // Very old browsers: fall back to the button
        return;
      }

      if (!this.observer && this.sentinel) {
        this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: `0px 0px ${this.offset}px 0px` });
      }

      // Re-observing forces a fresh callback, so short pages keep filling until the sentinel leaves the viewport
      if (this.observer && this.sentinel) {
        this.observer.unobserve(this.sentinel);
        this.observer.observe(this.sentinel);
      }
    }

    onIntersect(entries) {
      if (this.paused || this.isLoading) return;
      if (entries.some((entry) => entry.isIntersecting)) {
        this.loadNext().then(() => this.arm());
      }
    }

    onButtonClick() {
      this.paused = false;
      this.loadNext().then(() => this.arm());
    }

    onRetryClick() {
      this.paused = false;
      if (this.errorBox) this.errorBox.hidden = true;
      this.loadNext().then(() => this.arm());
    }

    // --- Loading ------------------------------------------------------------

    async loadNext({ silent = false } = {}) {
      const url = this.nextUrl;

      // Locks: one request in flight, never the same URL twice
      if (this.isLoading || !url || !this.isConnected) return;
      if (this.requestedUrls.has(url)) {
        debug('skip already requested', url);
        this.nextUrl = null;
        this.finish();
        return;
      }

      this.isLoading = true;
      this.requestedUrls.add(url);
      this.setLoading(true);
      this.abortController = new AbortController();

      let page;

      try {
        page = await this.fetchPage(url, this.abortController.signal);
      } catch (error) {
        this.isLoading = false;
        if (error && error.name === 'AbortError') {
          this.setLoading(false, { updateBar: false });
          return;
        }
        this.setLoading(false);
        this.requestedUrls.delete(url); // Allow the retry button to request it again
        this.showError();
        return;
      }

      this.isLoading = false;
      this.setLoading(false);
      if (!this.isConnected) return;

      const added = this.appendCards(page.cards);
      this.loadedUrls.push(url);
      this.nextUrl = page.nextUrl && !this.requestedUrls.has(page.nextUrl) ? page.nextUrl : null;

      debug(`page ${pageNumberOf(url)}: +${added} cards, ${this.renderedKeys.size} unique total`, this.total ? `of ${this.total}` : '');

      // Page only had duplicates: hop to the next one, capped to avoid loops
      if (added === 0 && this.nextUrl) {
        this.emptyHops += 1;
        if (this.emptyHops < MAX_EMPTY_HOPS) return this.loadNext({ silent });
        this.emptyHops = 0;
        this.paused = true;
        return;
      }

      this.emptyHops = 0;
      this.updateProgress();
      if (!silent && added > 0) this.announce(added);
      if (!this.nextUrl) this.finish();
    }

    // Section Rendering API: only this section's HTML is returned, never the full page
    async fetchPage(url, signal) {
      const requestUrl = new URL(url);
      requestUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(requestUrl.toString(), { signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = doc.getElementById(`product-list-${this.sectionId}`) || doc.querySelector('product-list');
      const cards = list ? Array.from(list.querySelectorAll('product-card')) : [];

      // Next URL: our own element first, then the pagination link, then page + 1 as a last resort
      let nextUrl = null;
      const element = doc.querySelector('zarif-infinite-scroll');
      if (element) {
        nextUrl = normaliseUrl(element.getAttribute('next-url'));
      } else {
        const nextLink = doc.querySelector('a[rel="next"]');
        if (nextLink) {
          nextUrl = normaliseUrl(nextLink.getAttribute('href'));
        } else if (pageNumberOf(url) < this.totalPages) {
          // e.g. a search page with only articles renders no product tab at all
          const fallback = new URL(url);
          fallback.searchParams.set('page', String(pageNumberOf(url) + 1));
          nextUrl = normaliseUrl(fallback.toString());
        }
      }

      return { cards, nextUrl };
    }

    // Append new cards only, imported so Prestige custom elements upgrade like on page 1
    appendCards(cards) {
      const fragment = document.createDocumentFragment();
      const added = [];

      cards.forEach((card) => {
        const key = cardKey(card);
        if (!key || this.renderedKeys.has(key)) return;
        this.renderedKeys.add(key);

        const node = document.importNode(card, true);
        fragment.appendChild(node);
        added.push(node);
      });

      if (!added.length) return 0;

      this.grid.appendChild(fragment);
      this.revealCards(added);
      this.refreshCards(added);

      // Lets other scripts (e.g. the currency converter refresh, snippets/zarif-currency-refresh.liquid) react
      document.dispatchEvent(new CustomEvent('zarif:products-appended', { detail: { cards: added, grid: this.grid } }));
      return added.length;
    }

    // Same reveal as Prestige's <product-list> (opacity + 20px rise, 200ms, 50ms stagger)
    revealCards(nodes) {
      const animate = prefersMotion();
      let index = 0;

      nodes.forEach((node) => {
        const shouldReveal = node.getAttribute('reveal-on-scroll') === 'true';
        node.removeAttribute('reveal-on-scroll'); // Otherwise Prestige CSS keeps it at opacity 0

        if (shouldReveal && animate && typeof node.animate === 'function') {
          node.animate(
            [{ opacity: 0, transform: 'translateY(20px)' }, { opacity: 1, transform: 'translateY(0)' }],
            { duration: 200, easing: 'ease-in-out', delay: Math.min(index, 12) * 50, fill: 'backwards' }
          );
          index += 1;
        }
      });
    }

    // Re-run custom card scripts (quick add, sizes) and sync wishlist hearts on appended cards
    refreshCards(nodes) {
      try {
        document.dispatchEvent(new CustomEvent('product-card:refresh', { detail: { root: this.grid } }));
      } catch (error) { /* no-op */ }

      try {
        const wishlist = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
        if (!Array.isArray(wishlist) || !wishlist.length) return;
        nodes.forEach((node) => {
          node.querySelectorAll('[data-handle], [data-product-handle]').forEach((button) => {
            const active = wishlist.includes(button.dataset.handle || button.dataset.productHandle);
            button.classList.toggle('active', active);
            button.setAttribute('aria-pressed', String(active));
          });
        });
      } catch (error) { /* no-op */ }
    }

    // --- Filters, sort and editor -------------------------------------------

    // Prestige emits facet:update before replacing the whole section: abort and reset immediately
    onFacetUpdate(event) {
      const url = event.detail && event.detail.url;
      const sectionId = url && url.searchParams ? url.searchParams.get('section_id') : null;
      if (sectionId && sectionId !== this.sectionId) return;

      this.clearState();
      this.reset();
    }

    onSectionUnload(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) this.teardown();
    }

    // Fallback: if something replaces <product-list> without replacing this element, re-seed from the new grid
    watchGrid() {
      const parent = this.grid.parentElement;
      if (!parent || !('MutationObserver' in window)) return;

      this.gridObserver = new MutationObserver(() => {
        const grid = document.getElementById(`product-list-${this.sectionId}`);
        if (!grid || grid === this.grid) return;
        debug('grid replaced, re-seeding');
        this.grid.removeEventListener('click', this.onGridClick, true);
        this.grid = grid;
        this.grid.addEventListener('click', this.onGridClick, true);
        this.reset();
        this.arm();
      });

      this.gridObserver.observe(parent, { childList: true });
    }

    reset() {
      this.abortController?.abort();
      this.abortController = null;
      this.isLoading = false;
      this.paused = false;
      this.emptyHops = 0;
      this.requestedUrls.clear();
      this.loadedUrls = [];
      this.setLoading(false, { updateBar: false });
      if (this.errorBox) this.errorBox.hidden = true;
      this.seed();
    }

    // --- UI -----------------------------------------------------------------

    // updateBar: false on abort/reset, so we never end the loading bar of a facet request Prestige started
    setLoading(loading, { updateBar = true } = {}) {
      if (this.loader) this.loader.hidden = !loading;

      if (this.button) {
        this.button.disabled = loading;
        loading ? this.button.setAttribute('aria-busy', 'true') : this.button.removeAttribute('aria-busy');
      }

      // Prestige's top loading bar (it has no counter, so only end what we started)
      if (loading && updateBar && !this.barActive) {
        this.barActive = true;
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      } else if (!loading) {
        if (this.barActive && updateBar) {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
        this.barActive = false;
      }
    }

    showButton(visible) {
      if (this.button) this.button.hidden = !visible;
    }

    showError() {
      this.paused = true;
      this.showButton(false);
      if (this.errorBox) this.errorBox.hidden = false;
    }

    updateProgress() {
      if (!this.progress || !this.progressTemplate || !this.total) return;
      const shown = Math.min(this.startOffset + this.renderedKeys.size, this.total);
      this.progress.textContent = this.progressTemplate.replace('[shown]', shown).replace('[total]', this.total);
    }

    announce(count) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.setTimeout(() => {
        if (this.liveRegion) this.liveRegion.textContent = `${count} more products loaded`;
      }, 100);
    }

    // No more pages: remove sentinel and loader, show the end line, show the footer again
    finish() {
      this.nextUrl = null;
      this.observer?.disconnect();
      this.observer = null;
      this.sentinel?.remove();
      this.loader?.remove();
      this.showButton(false);
      if (this.progress) this.progress.hidden = true;
      if (this.endMessage && this.loadedUrls.length > 0) this.endMessage.hidden = false;
      document.documentElement.classList.remove('zarif-is-hide-footer');
    }

    // --- Back button / scroll restoration -----------------------------------

    onGridClick(event) {
      const link = event.target.closest && event.target.closest('a[href]');
      if (link) this.saveState();
    }

    saveState() {
      if (!this.grid) return;
      try {
        if (!this.loadedUrls.length) {
          sessionStorage.removeItem(stateKey());
          return;
        }
        sessionStorage.setItem(stateKey(), JSON.stringify({
          pageCount: this.loadedUrls.length,
          nextUrl: this.nextUrl,
          scrollY: window.scrollY,
          timestamp: Date.now()
        }));
      } catch (error) { /* Storage full or disabled: ignore */ }
    }

    clearState() {
      try {
        sessionStorage.removeItem(stateKey());
      } catch (error) { /* no-op */ }
    }

    readState() {
      try {
        const state = JSON.parse(sessionStorage.getItem(stateKey()) || 'null');
        if (!state || !state.pageCount || Date.now() - state.timestamp > STATE_TTL) {
          this.clearState();
          return null;
        }
        return state;
      } catch (error) {
        return null;
      }
    }

    // Re-fetch saved pages in sequence (still de-duplicated), then restore the scroll position
    async restore() {
      if (window.Shopify && window.Shopify.designMode) return false;
      if (!isBackForward()) return false;

      const state = this.readState();
      if (!state) return false;

      try {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      } catch (error) { /* no-op */ }

      while (this.isConnected && this.nextUrl && !this.paused && this.loadedUrls.length < state.pageCount) {
        const before = this.loadedUrls.length;
        await this.loadNext({ silent: true });
        if (this.loadedUrls.length === before) break; // Error or abort: stop restoring
      }

      if (!this.isConnected) return false;

      window.requestAnimationFrame(() => {
        window.scrollTo(0, state.scrollY || 0);
        try {
          if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
        } catch (error) { /* no-op */ }
      });

      return true;
    }
  }

  window.customElements.define('zarif-infinite-scroll', ZarifInfiniteScroll);
})();
